import { Box } from '@chakra-ui/react';

import { getFormatDateFromObject, getDateFromString, CustomHeading } from 'shared-components';
import {
  OrgType,
  OrganisationSponsorshipMap,
  ScholarshipSponsorshipMap,
  EducationSponsorshipMap,
  FamilySponsorshipMap,
  OtherSponsorshipMap,
  IRisk,
} from 'types';

import { formatCurrency } from '../../../utilities/formatting';

import { SummaryRow } from '../../Summary/SummaryRow';
import { isFundingRisk } from '../../../utilities/risks';
import { hashToArray } from '../../../utilities/helpers';
import { isLoanTypeUnknown, loanTypeLabel } from '../../organisms/finance/EducationSponsor/utils';
import { coversLiving } from '../../../utilities/sponsors';
import { familySponsorTypeLabelShort, accountTypeLabel } from '../../organisms/finance/BankSponsor/utils';
import { SummaryGroup } from '../../Summary/SummaryGroup';
import { useTranslation } from "react-i18next";

interface Props {
  showRisks: boolean;
  organisationSponsorship: OrganisationSponsorshipMap;
  scholarshipSponsorship: ScholarshipSponsorshipMap;
  educationSponsorship: EducationSponsorshipMap;
  familySponsorship: FamilySponsorshipMap;
  otherSponsorship: OtherSponsorshipMap;
  risk: IRisk;
  courseFeeDepositReqd: string;
  courseFeeDeposit: string;
  courseFeeDepositDate: string;
  courseFeeDepositNotReqdReason: string;
  accomDepositReqd: string;
  accomDeposit: string;
  accomDepositDate: string;
}

function Finance({
  showRisks,
  organisationSponsorship,
  scholarshipSponsorship,
  educationSponsorship,
  familySponsorship,
  otherSponsorship,
  risk,
  courseFeeDepositReqd,
  courseFeeDeposit,
  courseFeeDepositDate,
  courseFeeDepositNotReqdReason,
  accomDepositReqd,
  accomDeposit,
  accomDepositDate,
}: Props) {
    const { t } = useTranslation('forms.finance');
  const getSponsorshipCovers = (covers: Array<string>) => {
    return covers
      ? covers.map((item: string, index: number) => (
          <span key={`${item}_${index}`}>
            {item === 'studentLivingCost'
              ? 'Student Living Cost'
              : item === 'spouseLivingCost'
                ? 'Partner Living Cost'
                : item === 'childrenLivingCost'
                  ? 'Children Living Cost'
                  : item}
            <br />
          </span>
        ))
      : null;
  };

  const getContributionSummary = (contributionType: string, index: number, contribution: any) => {
    return (
      <>
        <SummaryRow
          key={`${contributionType}_covering_type_${index}`}
          label={t('forms.finance:covering')}
          fullLabel={`What is the ${contributionType} covering?`}
          value={getSponsorshipCovers(contribution.sponsorshipCovers)}
          isRisk={showRisks && false}
        />

        {contribution.sponsorshipCovers && coversLiving(contribution.sponsorshipCovers) ? (
          <>
            {contribution.courseAndLivingFundSeparate ? (
              <>
                {contribution.livingFundType === 'Full' ? (
                  <SummaryRow
                    key={`${contributionType}_upfront_total_${index}`}
                    label={t('forms.finance:livingCosts')}
                    fullLabel="What is the total amount for living costs?"
                    value={contribution.livingFundType}
                    isRisk={showRisks && false}
                  />
                ) : null}

                {contribution.livingFundType === 'Percentage' ? (
                  <SummaryRow
                    key={`${contributionType}_percentage_total_${index}`}
                    label={t('forms.finance:livingCosts')}
                    fullLabel="What percentage is being covered for living costs?"
                    value={`${contribution.livingFundPercentage}%`}
                    isRisk={showRisks && false}
                  />
                ) : null}

                {contribution.livingFundType === 'Amount' ? (
                  <>
                    <SummaryRow
                      key={`${contributionType}_amount_type_${index}`}
                      label={t('forms.finance:livingCosts')}
                      fullLabel={`What is the ${
                        contribution.livingFundAmountType === 'Total' ? 'total' : 'monthly'
                      } amount for living costs?`}
                      value={`£${formatCurrency(contribution.livingFundAmount || '')}${
                        contribution.livingFundAmountType === 'Monthly' ? '/mon' : ''
                      }`}
                      isRisk={showRisks && false}
                    />
                  </>
                ) : null}
              </>
            ) : (
              <>
                {contribution.courseAndLivingFundType === 'Full' ? (
                  <SummaryRow
                    key={`${contributionType}_upfront_total_${index}`}
                    label={t('forms.finance:tuitionFeesAndLivingCosts')}
                    fullLabel="How much of your tuition fees and living costs does this sponsorship cover?"
                    value={contribution.courseAndLivingFundType}
                    isRisk={showRisks && false}
                  />
                ) : null}

                {contribution.courseAndLivingFundType === 'Percentage' ? (
                  <SummaryRow
                    key={`${contributionType}_percentage_total_${index}`}
                    label={t('forms.finance:tuitionFeesAndLivingCosts')}
                    fullLabel="What percentage is being covered for your tuition fees and living costs?"
                    value={`${contribution.courseAndLivingFundPercentage}%`}
                    isRisk={showRisks && false}
                  />
                ) : null}

                {contribution.courseAndLivingFundType === 'Amount' ? (
                  <SummaryRow
                    key={`${contributionType}_amount_type_${index}`}
                    label={t('forms.finance:tuitionFeesAndLivingCosts')}
                    fullLabel={`What is the total amount for your tuition fees and living costs?`}
                    value={`£${formatCurrency(contribution.courseAndLivingFundAmount || '')}`}
                    isRisk={showRisks && false}
                  />
                ) : null}
              </>
            )}
          </>
        ) : null}

        {contribution.courseFundType === 'Full' ? (
          <SummaryRow
            key={`${contributionType}_upfront_total_${index}`}
            label={t('forms.finance:tuitionFees')}
            fullLabel="How much of the tuition fees does the sponsorship cover?"
            value={contribution.courseFundType}
            isRisk={showRisks && false}
          />
        ) : null}

        {contribution.courseFundType === 'Percentage' ? (
          <SummaryRow
            key={`${contributionType}_percentage_total_${index}`}
            label={t('forms.finance:tuitionFees')}
            fullLabel="What percentage is being covered for your tuition fees?"
            value={`${contribution.courseFundPercentage}%`}
            isRisk={showRisks && false}
          />
        ) : null}

        {contribution.courseFundType === 'Amount' ? (
          <SummaryRow
            key={`${contributionType}_amount_type_${index}`}
            label={t('forms.finance:tuitionFees')}
            fullLabel={`What is the total amount for your tuition fees?`}
            value={`£${formatCurrency(contribution.courseFundAmount || '')}`}
            isRisk={showRisks && false}
          />
        ) : null}

        {contribution.sponsorshipAmount ? (
          <SummaryRow
            key={`${contributionType}_amount_covering_in_pounds_${index}`}
            label={t('forms.finance:contributionAmount')}
            fullLabel="What amount is being covered?"
            value={`£${formatCurrency(contribution.sponsorshipAmount)}`}
            isRisk={showRisks && false}
          />
        ) : null}
      </>
    );
  };

  return (
    <>
      <SummaryRow
        key="courseFeeDepositReqd"
        label={t('forms.finance:courseFeeDepositPaid')}
        value={courseFeeDepositReqd}
        isRisk={showRisks && false}
      />

      {courseFeeDepositReqd === 'Yes' && (
        <SummaryRow
          key="courseFeeDeposit"
          label={t('forms.finance:courseFeeDepositAmount')}
          value={courseFeeDeposit}
          isRisk={showRisks && false}
        />
      )}

      {courseFeeDepositReqd === 'No' && (
        <SummaryRow
          key="courseFeeDepositDate"
          label={t('forms.finance:expectedCourseFeeDepositDate')}
          fullLabel="When do you expect to pay your course fee deposit"
          value={getFormatDateFromObject(getDateFromString(courseFeeDepositDate))}
          isRisk={showRisks && false}
        />
      )}

      {courseFeeDepositReqd === 'No - Not Required' && (
        <SummaryRow
          key="courseFeeDepositNotReqdReason"
          label={t('forms.finance:courseFeeDepositReason')}
          fullLabel="Please provide more information on why a course fee deposit isn't required"
          value={courseFeeDepositNotReqdReason}
          isRisk={showRisks && false}
        />
      )}

      <SummaryRow
        key="accomDepositReqd"
        label={t('forms.finance:accommodationDepositPaid')}
        value={accomDepositReqd}
        isRisk={showRisks && false}
      />

      {accomDepositReqd === 'Yes' && (
        <SummaryRow
          key="accomDeposit"
          label={t('forms.finance:accommodationDepositAmount')}
          value={accomDeposit}
          isRisk={showRisks && false}
        />
      )}

      {accomDepositReqd === 'Not yet - I plan on paying one' && (
        <SummaryRow
          key="accomDepositDate"
          label={t('forms.finance:expectedAccommodationDepositDate')}
          fullLabel="When do you expect to pay your accommodation deposit"
          value={getFormatDateFromObject(getDateFromString(accomDepositDate))}
          isRisk={showRisks && false}
        />
      )}

      {Object.keys(organisationSponsorship).length > 0 && (
        <Box key={'sponsorship'} mt={6}>
          <CustomHeading level="h4" isAllCaps>
            {t('forms.finance:companyGovernmentSponsorship')}{Object.keys(organisationSponsorship).length > 1 ? 's' : null}
          </CustomHeading>

          {hashToArray(organisationSponsorship).map(({ key, item: sponsor }, index) => {
            return (
              <SummaryGroup
                title={sponsor.sponsorName}
                key={`organisation_sponsor_${key}_${index}`}
                boxProps={{ as: 'div' }}
              >
                <SummaryRow
                  key={`organisation_sponsor_type_${key}_${index}`}
                  label={t('forms.finance:organisationType')}
                  fullLabel="What type of organisation is sponsoring you?"
                  value={sponsor.orgType || ''}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.organisationSponsorship, 'orgType', key)}
                />

                <SummaryRow
                  key={`organisation_sponsor_name_${key}_${index}`}
                  label={t('forms.finance:sponsorName')}
                  fullLabel="What is the name of your sponsor or sponsorship?"
                  value={sponsor.sponsorName || ''}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.organisationSponsorship, 'sponsorName', key)}
                />

                {[OrgType.INT_ORG, OrgType.INT_COMP].includes(sponsor.orgType!) ? (
                  <SummaryRow
                    key={`organisation_sponsor_presence_${key}_${index}`}
                    label={t('forms.finance:presenceInMoreThanOneCountry')}
                    fullLabel="Does the Organisation or Company have a presence in more than one country?"
                    value={sponsor.isMultiCountry ? 'Yes' : 'No'}
                    isRisk={showRisks && isFundingRisk(risk.fundingRisk.organisationSponsorship, 'isMultiCountry', key)}
                  />
                ) : null}

                {getContributionSummary('sponsorship', index, sponsor!)}
              </SummaryGroup>
            );
          })}
        </Box>
      )}

      {Object.keys(scholarshipSponsorship).length > 0 && (
        <Box key={'scholarship'} mt={6}>
          <CustomHeading level="h4" isAllCaps>
            {t('forms.finance:universityScholarship')}{Object.keys(scholarshipSponsorship).length > 1 ? 's' : null}
          </CustomHeading>

          {hashToArray(scholarshipSponsorship).map(({ key, item: sponsor }, index) => {
            return (
              <SummaryGroup
                title={sponsor.scholarshipName}
                key={`scholarship_sponsor_${key}_${index}`}
                boxProps={{ as: 'div' }}
              >
                <SummaryRow
                  key={`scholarship_sponsor_name_${key}_${index}`}
                  label={t('forms.finance:name')}
                  fullLabel="Scholarship Name"
                  value={sponsor.scholarshipName!}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.scholarshipSponsorship, 'scholarshipName', key)}
                />

                {getContributionSummary('scholarship', index, sponsor!)}
              </SummaryGroup>
            );
          })}
        </Box>
      )}

      {Object.keys(educationSponsorship).length > 0 && (
        <Box key={'education'} mt={6}>
          <CustomHeading level="h4" isAllCaps>
            {t('forms.finance:educationLoan')}{Object.keys(educationSponsorship).length > 1 ? 's' : null}
          </CustomHeading>

          {hashToArray(educationSponsorship).map(({ key, item: sponsor }, index) => {
            return (
              <SummaryGroup title={sponsor.loanName} key={`education_sponsor_${key}_${index}`} boxProps={{ as: 'div' }}>
                {isLoanTypeUnknown(sponsor.loanType) ? (
                  <SummaryRow
                    key={`education_sponsor_desc_${key}_${index}`}
                    label={t('forms.finance:loanDescription')}
                    fullLabel="Please describe your loan below"
                    value={sponsor.loanDesc || ''}
                    isRisk={showRisks && true}
                  />
                ) : (
                  <SummaryRow
                    key={`education_sponsor_type_${key}_${index}`}
                    label={t('forms.finance:loanType')}
                    fullLabel="Which of the below best describes your loan?"
                    value={loanTypeLabel(sponsor.loanType || '')}
                    isRisk={showRisks && isFundingRisk(risk.fundingRisk.educationSponsorship, 'loanType', key)}
                  />
                )}

                <SummaryRow
                  key={`education_sponsor_name_${key}_${index}`}
                  label={t('forms.finance:loanName')}
                  fullLabel="What is the name of the loan?"
                  value={sponsor.loanName || ''}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.educationSponsorship, 'loanType', key)}
                />

                <SummaryRow
                  key={`education_sponsor_name_on_${key}_${index}`}
                  label={t('forms.finance:nameOnLoan')}
                  fullLabel="Whose name is on the loan?"
                  value={sponsor.personName!}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.educationSponsorship, 'personName', key)}
                />

                {sponsor.personName === 'Other' ? (
                  <SummaryRow
                    key={`education_sponsor_relationship_${key}_${index}`}
                    label={t('forms.finance:relationshipToPerson')}
                    fullLabel="What is your relationship to the person?"
                    value={sponsor.personDesc!}
                    isRisk={showRisks && true}
                  />
                ) : null}

                <SummaryRow
                  key={`education_sponsor_country_${key}_${index}`}
                  label={t('forms.finance:country')}
                  fullLabel="Country loan provider is located"
                  value={sponsor.country!}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.educationSponsorship, 'country', key)}
                />

                <SummaryRow
                  key={`education_sponsor_provider_${key}_${index}`}
                  label={t('forms.finance:loanProviderName')}
                  value={sponsor.bankName!}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.educationSponsorship, 'bankName', key)}
                />
                {getContributionSummary('education_loan', index, sponsor!)}
              </SummaryGroup>
            );
          })}
        </Box>
      )}

      {Object.keys(familySponsorship).length > 0 && (
        <Box key={'family'} mt={6}>
          <CustomHeading level="h4" isAllCaps>
            {t('forms.finance:ownOrFamily')}
                                </CustomHeading>

          {hashToArray(familySponsorship).map(({ key, item: sponsor }, index) => {
            return (
              <SummaryGroup
                title={accountTypeLabel(sponsor.accountType!)}
                key={`family_sponsorship_${key}_${index}`}
                boxProps={{ as: 'div' }}
              >
                <SummaryRow
                  key={`family_sponsor_owner_${key}_${index}`}
                  label={t('forms.finance:bankAccountOwner')}
                  fullLabel="Whose bank account?"
                  value={accountTypeLabel(sponsor.accountType!)}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.familySponsorship, 'accountType', key)}
                />

                <SummaryRow
                  key={`family_sponsor_acc_type_${key}_${index}`}
                  label={t('forms.finance:accountType')}
                  fullLabel="What is the account type?"
                  value={familySponsorTypeLabelShort(sponsor.sponsorType!)}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.familySponsorship, 'sponsorType', key)}
                />

                {sponsor.accountType === 'Other' && (
                  <SummaryRow
                    key={`family_sponsor_other_${key}_${index}`}
                    label={t('forms.finance:reasonForChoosingOther')}
                    fullLabel="Please explain why you've chosen 'other'?"
                    value={sponsor.otherReason!}
                    isRisk={showRisks && isFundingRisk(risk.fundingRisk.familySponsorship, 'otherReason', key)}
                  />
                )}

                <SummaryRow
                  key={`family_sponsor_bank_acc_${key}_${index}`}
                  label={t('forms.finance:bankAccountCountry')}
                  fullLabel="Country bank account is located"
                  value={sponsor.country!}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.familySponsorship, 'country', key)}
                />

                {sponsor.bankName ? (
                  <SummaryRow
                    key={`family_sponsor_bank_name_${key}_${index}`}
                    label={t('forms.finance:bankName')}
                    fullLabel="Please select the bank"
                    value={sponsor.bankName}
                    isRisk={showRisks && isFundingRisk(risk.fundingRisk.familySponsorship, 'bankName', key)}
                  />
                ) : null}

                <SummaryRow
                  key={`family_sponsor_bank_bal_${key}_${index}`}
                  label={t('forms.finance:currentBankBalance')}
                  fullLabel="What is the current balance of the account in pounds?"
                  value={`£${formatCurrency(sponsor.totalAmount!)}`}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.familySponsorship, 'totalAmount', key)}
                />
                {getContributionSummary('family_sponsor', index, sponsor)}
              </SummaryGroup>
            );
          })}
        </Box>
      )}

      {Object.keys(otherSponsorship).length > 0 && (
        <Box key={'otherSponsorship'} mt={6}>
          <CustomHeading level="h4" isAllCaps>
            {t('forms.finance:other')}
                                </CustomHeading>

          {hashToArray(otherSponsorship).map(({ key, item: sponsor }, index) => {
            return (
              <SummaryGroup
                title={sponsor.otherName}
                key={`other_sponsorship_${key}_${index}`}
                boxProps={{ as: 'div' }}
              >
                <SummaryRow
                  key={`other_sponsorship_expl_${key}_${index}`}
                  label={t('forms.finance:explanation')}
                  fullLabel="Please describe why this funding source does not fit into the other categories"
                  value={sponsor.otherDesc}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.other, 'other', key)}
                />

                <SummaryRow
                  key={`other_sponsorship_source_${key}_${index}`}
                  label={t('forms.finance:fundingSource')}
                  fullLabel="Please name this funding source"
                  value={sponsor.otherName}
                  isRisk={showRisks && isFundingRisk(risk.fundingRisk.other, 'other', key)}
                />

                {getContributionSummary('other', index, sponsor!)}
              </SummaryGroup>
            );
          })}
        </Box>
      )}
    </>
  );
}

export default Finance;
